/**
 * Fun, whimsical unit comparisons for total lifting volume.
 *
 * Given total lbs lifted, returns 3 playful comparisons scaled to
 * feel impressive but believable. Example outputs:
 *   "3.2 alpacas"      "0.5 polar bears"     "45 bowling balls"
 */

const UNITS = [
  { name: "housecat",         plural: "housecats",         lbs: 10,       emoji: "🐈" },
  { name: "bowling ball",     plural: "bowling balls",     lbs: 14,       emoji: "🎳" },
  { name: "gold bar",         plural: "gold bars",         lbs: 27,       emoji: "🥇" },
  { name: "golden retriever", plural: "golden retrievers", lbs: 70,       emoji: "🐕" },
  { name: "cheetah",          plural: "cheetahs",          lbs: 120,      emoji: "🐆" },
  { name: "alpaca",           plural: "alpacas",           lbs: 150,      emoji: "🦙" },
  { name: "panda",            plural: "pandas",            lbs: 220,      emoji: "🐼" },
  { name: "baby elephant",    plural: "baby elephants",    lbs: 250,      emoji: "🐘" },
  { name: "grizzly bear",     plural: "grizzly bears",     lbs: 600,      emoji: "🐻" },
  { name: "baby grand piano", plural: "baby grand pianos", lbs: 800,      emoji: "🎹" },
  { name: "polar bear",       plural: "polar bears",       lbs: 1000,     emoji: "🐻‍❄️" },
  { name: "moose",            plural: "moose",             lbs: 1500,     emoji: "🫎" },
  { name: "rhinoceros",       plural: "rhinoceroses",      lbs: 3000,     emoji: "🦏" },
  { name: "Honda Civic",      plural: "Honda Civics",      lbs: 2900,     emoji: "🚗" },
  { name: "hippo",            plural: "hippos",            lbs: 3300,     emoji: "🦛" },
  { name: "T-Rex",            plural: "T-Rexes",           lbs: 15000,    emoji: "🦖" },
  { name: "African elephant", plural: "African elephants", lbs: 12000,    emoji: "🐘" },
  { name: "school bus",       plural: "school buses",      lbs: 24000,    emoji: "🚌" },
  { name: "blue whale",       plural: "blue whales",       lbs: 330000,   emoji: "🐋" },
  { name: "Statue of Liberty",plural: "Statues of Liberty",lbs: 450000,   emoji: "🗽" },
  { name: "redwood tree",     plural: "redwood trees",     lbs: 1000000,  emoji: "🌲" },
];

/**
 * Format a quotient to feel natural:
 *   ≥ 10 → whole number ("12 alpacas")
 *   ≥ 1  → 1 decimal   ("3.2 alpacas")
 *   < 1  → 1 decimal   ("0.5 redwood trees")
 */
function formatQuantity(q) {
  if (q >= 10) return Math.round(q).toString();
  if (q >= 1) return q.toFixed(1).replace(/\.0$/, "");
  return q.toFixed(2).replace(/0$/, "").replace(/\.$/, "");
}

/**
 * Return whether a unit feels "good" for this volume — i.e. the
 * resulting multiplier is in a delightful range.
 *   0.25 → "half a redwood tree" — epic
 *   1–50 → "12 alpacas" — cute/relatable
 *   > 200 → "1,200 housecats" — numbers too big to visualize
 */
function isGoodFit(lbs, unit) {
  const q = lbs / unit.lbs;
  if (unit.lbs >= 200000) return q >= 0.2 && q <= 5;    // epic items
  if (unit.lbs >= 5000) return q >= 0.3 && q <= 20;
  return q >= 0.5 && q <= 80;                            // small/medium items
}

/**
 * Return 3 unique playful comparisons for a given total lbs lifted.
 * Picks one "small", one "medium", one "epic" unit for variety.
 * Returns [] if volume is 0.
 */
export function getVolumeComparisons(lbs) {
  if (!lbs || lbs <= 0) return [];

  const small = UNITS.filter((u) => u.lbs < 500 && isGoodFit(lbs, u));
  const medium = UNITS.filter((u) => u.lbs >= 500 && u.lbs < 5000 && isGoodFit(lbs, u));
  const epic = UNITS.filter((u) => u.lbs >= 5000 && isGoodFit(lbs, u));

  const pickRandom = (arr) =>
    arr.length ? arr[Math.floor(Math.random() * arr.length)] : null;

  const picks = [pickRandom(small), pickRandom(medium), pickRandom(epic)].filter(Boolean);

  // Fallback: if no good fits (tiny volume), pick the smallest unit.
  if (picks.length === 0) picks.push(UNITS[0]);

  return picks.map((u) => {
    const q = lbs / u.lbs;
    const qty = formatQuantity(q);
    const label = q === 1 ? u.name : u.plural;
    return { emoji: u.emoji, qty, label, raw: q };
  });
}

/**
 * Build a fun share caption like:
 *   "Just lifted 3.2 alpacas worth of weight on Paladin 💪"
 */
export function buildFunShareLine(lbs) {
  const comps = getVolumeComparisons(lbs);
  if (comps.length === 0) return null;
  const pick = comps[0];
  return `Just lifted ${pick.qty} ${pick.label} ${pick.emoji} worth of weight on Paladin`;
}
